import { Navigate, useNavigate } from 'react-router-dom';
import { getCompaniesForUser } from '@/mocks/api';
import { useAsync } from '@/hooks/useAsync';
import { useSession } from '@/state/session';
import { EmptyState, ErrorState, LoadingRegion, RowSkeleton } from '@/components/feedback';
import { Initials, ListRow, StatusBadge } from '@/components/banking';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';

/**
 * Company selection.
 *
 * TRACE:XS-SMEM-WR-032 — an accountant or shared-service user switches between
 * the companies they are authorised for without signing out. The selected
 * company then persists across navigation (see useSession).
 */
export function SelectCompanyPage() {
  const navigate = useNavigate();
  const userId = useSession((s) => s.userId);
  const selectCompany = useSession((s) => s.selectCompany);
  const returnTo = useSession((s) => s.returnTo);

  const companies = useAsync(async () => {
    if (!userId) return [];
    return (await getCompaniesForUser(userId)).data;
  }, [userId]);

  if (!userId) return <Navigate to="/mobile/login" replace />;

  const choose = (companyId: string) => {
    selectCompany(companyId);
    navigate(returnTo && returnTo.startsWith('/mobile/') ? returnTo : '/mobile/home');
  };

  return (
    <>
      <MobileHeader title="Choose a company" root />
      <div className="tm-stack" style={{ paddingBlockStart: 12 }}>
        <div>
          {/* Single <h1> comes from MobileHeader. */}
          <h2 className="tm-page-title">Which company?</h2>
          <p className="tm-muted" style={{ marginBlockStart: 6 }}>
            You have access to more than one. You can switch at any time.
          </p>
        </div>

        {companies.status === 'loading' ? (
          <LoadingRegion label="Loading your companies">
            <div className="tm-card tm-card--flush">
              <RowSkeleton count={3} />
            </div>
          </LoadingRegion>
        ) : null}

        {companies.status === 'error' ? (
          <ErrorState error={companies.error} onRetry={companies.reload} />
        ) : null}

        {companies.status === 'success' && companies.data.length === 0 ? (
          <EmptyState
            title="No companies yet"
            body="Your access has not been set up. Ask an administrator to invite you to a company."
          />
        ) : null}

        {companies.status === 'success' && companies.data.length > 0 ? (
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              {companies.data.map((company) => (
                <li key={company.id}>
                  <ListRow
                    onClick={() => choose(company.id)}
                    leading={<Initials name={company.tradingName} />}
                    title={company.tradingName}
                    meta={company.legalName}
                    end={
                      company.status === 'active' ? null : <StatusBadge status={company.status} />
                    }
                    ariaLabel={`${company.tradingName}, ${company.legalName}, status ${company.status}`}
                  />
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </>
  );
}
